import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { contentService } from '../services/contentService';
import { useSliceBalance } from '../hooks/useSliceBalance';
import { usePotNomination } from '../hooks/usePotNomination';
import DonateSlicesModal from '../components/DonateSlicesModal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../hooks/use-toast';
import { KinoraMark } from '../components/KinoraSidebar';
import {
  Play, Plus, Check, Clock,
  ChevronDown, ThumbsUp, ArrowLeft, ExternalLink, Flag, Sparkles
} from 'lucide-react';
import { cn } from '../lib/utils';
import { guildAuthUrl } from '../lib/guildUrl';
import ReportDialog from '../components/ReportDialog';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const ContentDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const { balance, isActiveSubscriber } = useSliceBalance();
  const [isDonateOpen, setIsDonateOpen] = useState(false);
  const [work, setWork] = useState(null);
  const [relatedContent, setRelatedContent] = useState([]);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSeason, setExpandedSeason] = useState(null);
  const [ratingCounts, setRatingCounts] = useState({ likes: 0, dislikes: 0 });
  const [reportOpen, setReportOpen] = useState(false);
  const [isNominating, setIsNominating] = useState(false);
  const {
    nominationCount,
    hasNominated,
    nominate,
  } = usePotNomination(work?.id);

  useDocumentMeta({
    title: work?.title,
    description: work?.description || undefined,
    image: work?.poster_url || work?.backdrop_url || undefined,
    url: window.location.href,
  });

  useEffect(() => {
    if (slug) {
      loadWork();
    }
  }, [slug]);

  useEffect(() => {
    if (work && user) {
      checkWatchlist();
    }
  }, [work, user]);

  const loadWork = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await contentService.getWorkBySlug(slug);

      if (!data) {
        setError('Content not found');
        return;
      }

      setWork(data);
      fetchRatingCounts(data.id);

      // Auto-expand first season for TV shows
      if (data.seasons?.length > 0) {
        setExpandedSeason(data.seasons[0].season_number);
      }

      // Fetch related content
      const related = await contentService.getRelatedContent(data.id, data.tags || []);
      setRelatedContent(related);
    } catch (err) {
      console.error('Error loading work:', err);
      setError('Failed to load content');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRatingCounts = async (workId) => {
    if (!workId) return;
    try {
      const { data } = await supabase
        .from('user_ratings')
        .select('rating_type')
        .eq('content_id', workId)
        .in('rating_type', ['like', 'dislike']);
      if (data) {
        setRatingCounts({
          likes: data.filter(r => r.rating_type === 'like').length,
          dislikes: data.filter(r => r.rating_type === 'dislike').length,
        });
      }
    } catch {
      // table may have no rows yet
    }
  };

  const checkWatchlist = async () => {
    if (!user || !work) return;
    try {
      const { data } = await supabase
        .from('user_watchlist')
        .select('id')
        .eq('user_id', user.id)
        .eq('work_id', work.id)
        .maybeSingle();
      setIsInWatchlist(!!data);
    } catch {
      setIsInWatchlist(false);
    }
  };

  const toggleWatchlist = useCallback(async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!work) return;

    try {
      if (isInWatchlist) {
        await supabase
          .from('user_watchlist')
          .delete()
          .eq('user_id', user.id)
          .eq('work_id', work.id);
        setIsInWatchlist(false);
        toast({ title: 'Removed from watchlist' });
      } else {
        await supabase
          .from('user_watchlist')
          .insert({ user_id: user.id, work_id: work.id });
        setIsInWatchlist(true);
        toast({ title: 'Added to watchlist' });
      }
    } catch (err) {
      console.error('Error toggling watchlist:', err);
      toast({ title: 'Failed to update watchlist', variant: 'destructive' });
    }
  }, [user, work, isInWatchlist, navigate, toast]);

  const handleNominate = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!work || hasNominated || isNominating) return;

    setIsNominating(true);
    try {
      await nominate();
      toast({
        title: '🍕 Nominated for the Community Fund',
        description: `"${work.title}" is in the running for this month's pot.`,
      });
    } catch (err) {
      toast({
        title: 'Nomination failed',
        description: err.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsNominating(false);
    }
  };

  const getPlayUrl = (videoId) => {
    return `/video/${videoId || work?.id}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m} min`;
  };

  const formatViews = (count) => {
    if (!count) return '0';
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return count.toString();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="relative w-full" style={{ aspectRatio: '21/9', maxHeight: '70vh' }}>
          <Skeleton className="w-full h-full" />
        </div>
        <div className="max-w-7xl mx-auto px-12 -mt-32 relative z-10 space-y-6">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-6 w-1/3" />
          <div className="flex gap-3">
            <Skeleton className="h-12 w-36 rounded-full" />
            <Skeleton className="h-12 w-36 rounded-full" />
          </div>
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  // Error / not found
  if (error || !work) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div
          className="max-w-lg w-full p-10 rounded-3xl text-center space-y-5"
          style={{
            background: 'hsl(var(--kinora-card-hi, var(--card)))',
            border: '1px solid hsl(var(--kinora-line-soft, var(--border)))',
          }}
        >
          <div className="text-[10px] tracking-[0.28em] uppercase text-muted-foreground">
            Reel missing
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Content not found</h2>
          <p className="text-muted-foreground">
            {error || "The film you're looking for has slipped from the projector."}
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Button variant="ghost" className="rounded-full" onClick={() => window.history.back()}>
              Go Back
            </Button>
            <Button className="rounded-full" onClick={() => navigate('/browse')}>
              Browse
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const directors = (work.contributors || []).filter(c => c.role === 'director');
  const actors = (work.contributors || []).filter(c => c.role === 'actor');
  const crew = (work.contributors || []).filter(c => c.role !== 'director' && c.role !== 'actor');
  const heroImage = work.backdrop_url || work.poster_url || work.thumbnail;
  const ratingPct = (ratingCounts.likes + ratingCounts.dislikes) > 0
    ? Math.round((ratingCounts.likes / (ratingCounts.likes + ratingCounts.dislikes)) * 100)
    : null;
  const directorLine = directors.length > 0
    ? directors.map(d => d.name).join(', ')
    : null;

  return (
    <div className="min-h-screen bg-background">
      {/* ── HERO ── */}
      <section className="relative px-6 md:px-12 pt-6">
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center gap-1.5 mb-4 text-[10px] tracking-[0.22em] uppercase text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="h-3 w-3" />
          Back
        </button>
        <div
          className="relative w-full overflow-hidden rounded-[20px]"
          style={{ aspectRatio: '21/9' }}
        >
          {heroImage ? (
            <img
              src={heroImage}
              alt={work.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/25 to-accent/30" />
          )}
          {/* Editorial scrim — left-weighted for legibility */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(100deg, hsl(var(--background) / 0.94) 0%, hsl(var(--background) / 0.55) 45%, transparent 80%)',
            }}
          />
          <div
            className="absolute inset-x-0 bottom-0 h-32"
            style={{
              background:
                'linear-gradient(180deg, transparent 0%, hsl(var(--background) / 0.85) 100%)',
            }}
          />

          <div className="relative z-10 h-full flex items-end p-7 md:p-14">
            <div className="max-w-2xl space-y-4">
              {/* Eyebrow */}
              <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-primary">
                <span>{work.type === 'tv_show' ? 'TV Show' : 'Film'}</span>
                {work.release_year && (
                  <>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <span>{work.release_year}</span>
                  </>
                )}
                {work.tags?.[0] && (
                  <>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <span>{work.tags[0]}</span>
                  </>
                )}
                {work.isNew && (
                  <>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <span className="text-foreground">New</span>
                  </>
                )}
              </div>

              {/* Title */}
              <h1
                className="leading-[0.98] tracking-tight text-foreground font-bold"
                style={{
                  fontSize: 'clamp(2.5rem, 6vw, 4.25rem)',
                  letterSpacing: '-0.035em',
                }}
              >
                {work.title}
              </h1>

              {/* Director byline (italic Fraunces) */}
              {directorLine && (
                <div
                  className="text-base md:text-lg"
                  style={{ color: 'hsl(var(--muted-foreground))' }}
                >
                  directed by{' '}
                  <span
                    style={{ color: 'hsl(var(--foreground))' }}
                  >
                    {directorLine}
                  </span>
                </div>
              )}

              {/* Mono meta strip */}
              <div className="flex items-center gap-3 text-[10px] tracking-[0.18em] uppercase tabular-nums text-muted-foreground flex-wrap">
                {work.duration && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {work.duration}
                  </span>
                )}
                {work.view_count > 0 && (
                  <>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span>{formatViews(work.view_count)} views</span>
                  </>
                )}
                {ratingPct !== null && (
                  <>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span className="inline-flex items-center gap-1">
                      <ThumbsUp className="h-3 w-3" />
                      {ratingPct}% liked
                    </span>
                  </>
                )}
              </div>

              {/* Description */}
              {work.description && (
                <p
                  className="text-sm md:text-[15px] leading-relaxed line-clamp-3 max-w-xl pt-1"
                  style={{ color: 'hsl(var(--kinora-fg-soft, var(--foreground)) / 0.9)' }}
                >
                  {work.description}
                </p>
              )}

              {/* Pill buttons */}
              <div className="flex flex-wrap items-center gap-2.5 pt-3">
                {/* Play */}
                <button
                  onClick={() => navigate(getPlayUrl(work.id))}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-[13px] transition-opacity hover:opacity-90"
                  style={{
                    background: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                  }}
                >
                  <Play className="h-3.5 w-3.5 fill-current" />
                  Play
                </button>

                {/* Watchlist */}
                <button
                  onClick={toggleWatchlist}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-[13px] transition-colors"
                  style={{
                    background: 'transparent',
                    color: 'hsl(var(--foreground))',
                    border: '1px solid hsl(var(--kinora-line-soft, var(--border)))',
                  }}
                >
                  {isInWatchlist ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  {isInWatchlist ? 'In watchlist' : 'Watchlist'}
                </button>

                {/* Slice donate (auth-gated) */}
                {!user ? (
                  <button
                    onClick={() => navigate('/auth')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-[13px] transition-colors"
                    style={{
                      background: 'hsl(var(--primary) / 0.16)',
                      color: 'hsl(var(--primary))',
                      border: '1px solid hsl(var(--primary) / 0.32)',
                    }}
                  >
                    <KinoraMark size={14} />
                    Sign in to slice
                  </button>
                ) : !isActiveSubscriber ? (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-[13px] transition-colors"
                    style={{
                      background: 'hsl(var(--primary) / 0.16)',
                      color: 'hsl(var(--primary))',
                      border: '1px solid hsl(var(--primary) / 0.32)',
                    }}
                  >
                    <KinoraMark size={14} />
                    Subscribe to slice
                  </button>
                ) : (
                  <button
                    onClick={() => balance > 0 && setIsDonateOpen(true)}
                    disabled={balance === 0}
                    title={balance === 0 ? 'No slices available this month' : undefined}
                    className={cn(
                      'inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-[13px] transition-colors',
                      balance === 0 && 'cursor-not-allowed opacity-60'
                    )}
                    style={{
                      background: 'hsl(var(--primary) / 0.16)',
                      color: 'hsl(var(--primary))',
                      border: '1px solid hsl(var(--primary) / 0.32)',
                    }}
                  >
                    <KinoraMark size={14} />
                    Give a Slice{balance !== null ? ` · ${balance}` : ''}
                  </button>
                )}

                {/* Nominate for the Community Fund (published works only) */}
                {work.status === 'published' && (
                  <button
                    onClick={handleNominate}
                    disabled={hasNominated || isNominating}
                    title={
                      hasNominated
                        ? 'You already nominated this work this month'
                        : 'Put this work forward for this month’s Community Fund'
                    }
                    className={cn(
                      'inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-[13px] transition-colors',
                      'bg-amber-500/10 text-amber-500 border border-amber-500/30',
                      hasNominated
                        ? 'cursor-default opacity-80'
                        : 'hover:bg-amber-500/20',
                      isNominating && 'opacity-60 cursor-wait'
                    )}
                  >
                    {hasNominated ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    {isNominating
                      ? 'Nominating…'
                      : hasNominated
                        ? 'Nominated'
                        : 'Nominate for the Community Fund'}
                    {nominationCount > 0 ? ` · ${nominationCount}` : ''}
                  </button>
                )}

                {/* Report */}
                <button
                  type="button"
                  onClick={() => user ? setReportOpen(true) : navigate('/auth')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.18em] uppercase text-muted-foreground hover:text-destructive transition-colors"
                  title="Report this content"
                >
                  <Flag className="h-3 w-3" />
                  Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Report dialog */}
      {work && (
        <ReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          workId={work.id}
        />
      )}

      {/* ── BODY ── */}
      <div className="px-6 md:px-12 py-14 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12">
          {/* Main Column */}
          <div className="space-y-12 min-w-0">
            {/* Genre tags */}
            {work.tags?.length > 0 && (
              <section>
                <div className="text-[10px] tracking-[0.28em] uppercase text-primary mb-3">
                  Tagged
                </div>
                <div className="flex flex-wrap gap-2">
                  {work.tags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => navigate(`/browse?genre=${tag}`)}
                      className="text-[10px] tracking-[0.18em] uppercase rounded-full px-3 py-1.5 transition-colors"
                      style={{
                        background: 'transparent',
                        color: 'hsl(var(--muted-foreground))',
                        border: '1px solid hsl(var(--kinora-line-soft, var(--border)))',
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* About */}
            {work.description && (
              <section>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-[10px] tracking-[0.28em] uppercase text-primary whitespace-nowrap">
                    Synopsis
                  </span>
                  <div className="h-px flex-1" style={{ background: 'hsl(var(--kinora-line-soft, var(--border)))' }} />
                </div>
                <h2
                  className="text-xl font-semibold tracking-tight mb-5"
                >
                  About this {work.type === 'tv_show' ? 'series' : 'film'}
                </h2>
                <p className="text-[15px] leading-relaxed text-muted-foreground max-w-3xl">
                  {work.description}
                </p>
              </section>
            )}

            {/* Seasons & Episodes for TV Shows */}
            {work.type === 'tv_show' && work.seasons?.length > 0 && (
              <section>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-[10px] tracking-[0.28em] uppercase text-primary whitespace-nowrap">
                    Episodes
                  </span>
                  <div className="h-px flex-1" style={{ background: 'hsl(var(--kinora-line-soft, var(--border)))' }} />
                </div>
                <h2
                  className="text-xl font-semibold tracking-tight mb-6"
                >
                  Seasons & episodes
                </h2>
                <div className="space-y-3">
                  {work.seasons.map((season) => (
                    <div
                      key={season.id}
                      className="overflow-hidden rounded-2xl"
                      style={{
                        background: 'hsl(var(--kinora-card-hi, var(--card)))',
                        border: '1px solid hsl(var(--kinora-line-soft, var(--border)))',
                      }}
                    >
                      <button
                        className="w-full flex items-center justify-between p-4 hover:bg-secondary/40 transition-colors text-left"
                        onClick={() => setExpandedSeason(
                          expandedSeason === season.season_number ? null : season.season_number
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] tracking-[0.22em] uppercase text-primary">
                            Season {String(season.season_number).padStart(2, '0')}
                          </span>
                          {season.title && (
                            <span className="text-base">{season.title}</span>
                          )}
                          <span className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                            {season.episodes?.length || 0} ep
                          </span>
                        </div>
                        <ChevronDown className={cn(
                          "h-4 w-4 text-muted-foreground transition-transform",
                          expandedSeason === season.season_number && "rotate-180"
                        )} />
                      </button>

                      {expandedSeason === season.season_number && season.episodes?.length > 0 && (
                        <div style={{ borderTop: '1px solid hsl(var(--kinora-line-soft, var(--border)))' }}>
                          {season.episodes.map((episode, i) => (
                            <div
                              key={episode.id}
                              className="grid grid-cols-[auto_112px_1fr_auto] gap-4 items-center p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
                              style={{
                                borderTop: i === 0 ? 'none' : '1px solid hsl(var(--kinora-line-soft, var(--border)))',
                              }}
                              onClick={() => navigate(`/video/${work.id}?episode=${episode.id}`)}
                            >
                              <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground tabular-nums">
                                E{String(episode.episode_number).padStart(2, '0')}
                              </span>
                              <div className="relative w-28 h-16 rounded-md overflow-hidden bg-muted">
                                {episode.thumbnail ? (
                                  <img src={episode.thumbnail} alt={episode.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Play className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="text-[15px] truncate">{episode.title}</div>
                                {episode.description && (
                                  <p className="text-xs text-muted-foreground line-clamp-1 mt-1">
                                    {episode.description}
                                  </p>
                                )}
                              </div>
                              {episode.duration_seconds && (
                                <span className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground tabular-nums">
                                  {formatDuration(episode.duration_seconds)}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Cast & Crew */}
            {work.contributors?.length > 0 && (
              <section>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-[10px] tracking-[0.28em] uppercase text-primary whitespace-nowrap">
                    Credits
                  </span>
                  <div className="h-px flex-1" style={{ background: 'hsl(var(--kinora-line-soft, var(--border)))' }} />
                </div>
                <h2
                  className="text-xl font-semibold tracking-tight mb-6"
                >
                  Cast & crew
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {directors.length > 0 && (
                    <div>
                      <div className="text-[9px] tracking-[0.28em] uppercase text-muted-foreground mb-3">
                        Director{directors.length > 1 ? 's' : ''}
                      </div>
                      <div className="space-y-3">
                        {directors.map(d => (
                          <div key={d.id || d.name} className="flex items-center gap-3">
                            {d.profile_image_url ? (
                              <img src={d.profile_image_url} alt={d.name} className="w-9 h-9 rounded-full object-cover" />
                            ) : (
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center text-sm"
                                style={{
                                  background: 'hsl(var(--primary) / 0.16)',
                                  color: 'hsl(var(--primary))',
                                }}
                              >
                                {d.name?.[0]}
                              </div>
                            )}
                            {d.user_id ? (
                              <Link to={`/creator/${d.user_id}`} className="text-[15px] hover:text-primary transition-colors">
                                {d.name}
                              </Link>
                            ) : (
                              <span className="text-[15px]">{d.name}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {actors.length > 0 && (
                    <div>
                      <div className="text-[9px] tracking-[0.28em] uppercase text-muted-foreground mb-3">
                        Cast
                      </div>
                      <div className="space-y-3">
                        {actors.slice(0, 8).map(a => (
                          <div key={a.id || a.name} className="flex items-center gap-3">
                            {a.profile_image_url ? (
                              <img src={a.profile_image_url} alt={a.name} className="w-9 h-9 rounded-full object-cover" />
                            ) : (
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center text-sm"
                                style={{
                                  background: 'hsl(var(--primary) / 0.16)',
                                  color: 'hsl(var(--primary))',
                                }}
                              >
                                {a.name?.[0]}
                              </div>
                            )}
                            <div className="min-w-0">
                              {a.user_id ? (
                                <Link to={`/creator/${a.user_id}`} className="text-[15px] hover:text-primary transition-colors block">
                                  {a.name}
                                </Link>
                              ) : (
                                <div className="text-[15px]">{a.name}</div>
                              )}
                              {a.character_name && (
                                <div className="text-[12px] text-muted-foreground">
                                  as {a.character_name}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {crew.length > 0 && (
                    <div>
                      <div className="text-[9px] tracking-[0.28em] uppercase text-muted-foreground mb-3">
                        Crew
                      </div>
                      <div className="space-y-3">
                        {crew.slice(0, 10).map(c => (
                          <div key={`${c.id || c.name}-${c.role}`} className="flex items-center gap-3">
                            {c.profile_image_url ? (
                              <img src={c.profile_image_url} alt={c.name} className="w-9 h-9 rounded-full object-cover" />
                            ) : (
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center text-sm"
                                style={{
                                  background: 'hsl(var(--primary) / 0.16)',
                                  color: 'hsl(var(--primary))',
                                }}
                              >
                                {c.name?.[0]}
                              </div>
                            )}
                            <div className="min-w-0">
                              {c.user_id ? (
                                <Link to={`/creator/${c.user_id}`} className="text-[15px] hover:text-primary transition-colors block">
                                  {c.name}
                                </Link>
                              ) : (
                                <div className="text-[15px]">{c.name}</div>
                              )}
                              <div className="text-[12px] text-muted-foreground">
                                {c.role}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-4 text-sm text-muted-foreground">
                        Are you a creator?{' '}
                        <a
                          href={guildAuthUrl()}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1"
                        >
                          Join the Kinora Guild
                        </a>
                        {' '}— this could be you.
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          {/* ── SIDEBAR ── */}
          <aside className="space-y-8">
            {/* Details Card */}
            <div
              className="rounded-2xl p-6 space-y-4"
              style={{
                background: 'hsl(var(--kinora-card-hi, var(--card)))',
                border: '1px solid hsl(var(--kinora-line-soft, var(--border)))',
              }}
            >
              <div className="text-[10px] tracking-[0.28em] uppercase text-primary">
                Details
              </div>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground self-center">Type</dt>
                  <dd className="text-[14px] capitalize">{work.type === 'tv_show' ? 'TV Show' : work.type}</dd>
                </div>
                {work.release_date && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground self-center">Released</dt>
                    <dd className="text-[14px]">{work.releaseDate}</dd>
                  </div>
                )}
                {work.duration && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground self-center">Runtime</dt>
                    <dd className="text-[14px]">{work.duration}</dd>
                  </div>
                )}
                {work.view_count > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground self-center">Views</dt>
                    <dd className="text-[14px] tabular-nums">{formatViews(work.view_count)}</dd>
                  </div>
                )}
                {ratingPct !== null && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground self-center">Rating</dt>
                    <dd className="text-[14px] inline-flex items-center gap-1.5">
                      <ThumbsUp className="h-3 w-3" />
                      {ratingPct}%
                      <span className="text-[10px] tracking-[0.12em] text-muted-foreground tabular-nums">
                        ({ratingCounts.likes + ratingCounts.dislikes})
                      </span>
                    </dd>
                  </div>
                )}
                {work.creator?.name && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground self-center">Studio</dt>
                    <dd className="text-[14px]">
                      <button
                        className="hover:text-primary transition-colors text-right"
                        onClick={() => navigate(`/studio/${work.creator.slug || work.creator.name}`)}
                      >
                        {work.creator.name}
                      </button>
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Related Content */}
            {relatedContent.length > 0 && (
              <div>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-[10px] tracking-[0.28em] uppercase text-primary whitespace-nowrap">
                    More like this
                  </span>
                  <div className="h-px flex-1" style={{ background: 'hsl(var(--kinora-line-soft, var(--border)))' }} />
                </div>
                <div className="space-y-2">
                  {relatedContent.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.slug ? `/watch/${item.slug}` : `/video/${item.id}`)}
                      className="w-full text-left grid grid-cols-[112px_1fr] gap-3 p-2 rounded-xl transition-colors hover:bg-secondary/40"
                    >
                      <div
                        className="relative w-28 h-16 rounded-md overflow-hidden flex-shrink-0"
                        style={{ background: 'hsl(var(--muted))' }}
                      >
                        {item.thumbnail ? (
                          <img
                            src={item.thumbnail}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex flex-col justify-center">
                        <div
                          className="text-[14px] leading-tight truncate font-medium"
                        >
                          {item.title}
                        </div>
                        <div className="text-[9px] tracking-[0.18em] uppercase text-muted-foreground mt-1 truncate">
                          {item.type === 'tv_show' ? 'TV Show' : item.type}
                          {item.tags?.[0] ? ` · ${item.tags[0]}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {work && (
        <DonateSlicesModal
          isOpen={isDonateOpen}
          onClose={() => setIsDonateOpen(false)}
          work={work}
          balance={balance}
          isActiveSubscriber={isActiveSubscriber}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
};

export default ContentDetail;
